import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useMutation, useQuery } from 'urql';
import DestructiveAction, {
  DestructiveActionHeading,
} from '@/components/Custom/DestructiveAction';
import ContentBoundary from '@/components/UI/ContentBoundary';
import EmptyPlaceholder from '@/components/UI/EmptyPlaceholder';
import { graphql } from '@/gql/graphql';
import useUserId from '@/hooks/useUserId';
import useRootStore from '@/stores/useRootStore';

const LeaveTeam = graphql(/* GraphQL */ `
  mutation LeaveTeam($personId: String!, $teamId: uuid!) {
    delete_person_team_by_pk(person_id: $personId, team_id: $teamId) {
      person_id
      team_id
    }
  }
`);

const GetTeamLeave = graphql(/* GraphQL */ `
  query GetTeamLeave($teamId: uuid!) {
    team_by_pk(id: $teamId) {
      id
      title
    }
  }
`);

export default function TeamLeave() {
  const personId = useUserId();

  const { teamId } = useLocalSearchParams<{
    teamId?: string;
  }>();

  const { replace } = useRouter();
  const [, leaveTeam] = useMutation(LeaveTeam);

  const [{ data, fetching, error }, refresh] = useQuery({
    query: GetTeamLeave,
    variables: { teamId: teamId! },
  });

  const team = data?.team_by_pk;

  const onPressLeave = async () => {
    const { data: mutationData, error } = await leaveTeam({
      personId,
      teamId: teamId!,
    });

    if (error || !mutationData?.delete_person_team_by_pk) {
      return { success: false };
    }
    return { success: true };
  };

  const onSuccess = () => {
    useRootStore.setState({
      activeContextPathname: null,
      activeTab: 'event',
      teamId: null,
    });
    replace('/event');
  };

  return (
    <>
      <Stack.Screen
        options={{ title: team?.title ? `Leave ${team.title}` : 'Leave' }}
      />
      <ContentBoundary
        data={team?.title ? { id: team.id, title: team.title } : null}
        loading={fetching}
        error={error}
        onRetry={() => refresh({ requestPolicy: 'network-only' })}
        empty={<EmptyPlaceholder message="Leaving is unavailable." />}
      >
        {(team) => (
          <DestructiveAction
            key={team.id}
            label="Leave team"
            submittingLabel="Leaving…"
            onAction={onPressLeave}
            onSuccess={onSuccess}
            testID="TeamLeaveButton"
          >
            <DestructiveActionHeading>
              Are you sure you want to leave {team.title}?
            </DestructiveActionHeading>
          </DestructiveAction>
        )}
      </ContentBoundary>
    </>
  );
}
